import { isTabAllowed } from '@revelio/auth';
import { Tab } from '@revelio/data-access';

import type { ChartType } from '../../PageConfigProvider/types/chart';
import type { SecondaryDimensionValues } from '../../PageConfigProvider/types/dimension';
import {
  isTalentDiscoveryMetric,
  type MetricValues,
} from '../../PageConfigProvider/types/metric';
import {
  routesToCompensation,
  routesToComposition,
  routesToPosting,
  routesToScreener,
  routesToSentiment,
  routesToTransition,
} from './data-provider-routing';

const DATA_SOURCE_ACCESS_MESSAGE = (dataSourceLabel: string) =>
  `This chart uses ${dataSourceLabel} data, which is not available for your account. Please contact sales to upgrade.`;

type ChartDataSourceAccessArgs = {
  metric: MetricValues | undefined;
  chartType: ChartType;
  secondaryDimension?: SecondaryDimensionValues;
  userTabs?: Tab[];
};

type DataSourceAccessRequirement = {
  tab: Tab;
  label: string;
};

function getChartDataSourceAccessRequirement({
  metric,
  chartType,
  secondaryDimension,
}: Omit<ChartDataSourceAccessArgs, 'userTabs'>):
  | DataSourceAccessRequirement
  | undefined {
  if (metric === undefined) {
    return undefined;
  }

  if (isTalentDiscoveryMetric(metric)) {
    return { tab: Tab.TalentDiscovery, label: 'Talent Discovery' };
  }

  if (routesToComposition(metric, chartType)) {
    return { tab: Tab.Overview, label: 'Compositions' };
  }

  if (routesToTransition(metric, chartType, secondaryDimension)) {
    return { tab: Tab.Transition, label: 'Transitions' };
  }

  if (routesToPosting(metric, chartType, secondaryDimension)) {
    return { tab: Tab.Posting, label: 'Job Postings' };
  }

  if (routesToSentiment(metric, chartType)) {
    return { tab: Tab.Sentiment, label: 'Sentiment' };
  }

  if (routesToCompensation(metric, chartType, secondaryDimension)) {
    return { tab: Tab.Compensation, label: 'Pay' };
  }

  if (routesToScreener(metric, chartType)) {
    return { tab: Tab.Screener, label: 'Screener' };
  }

  return undefined;
}

export function getChartDataSourceAccessErrorMessage({
  userTabs = [],
  ...args
}: ChartDataSourceAccessArgs): string | undefined {
  const requirement = getChartDataSourceAccessRequirement(args);

  if (requirement && !isTabAllowed(requirement.tab, userTabs)) {
    return DATA_SOURCE_ACCESS_MESSAGE(requirement.label);
  }

  return undefined;
}
